import { Injectable } from '@angular/core';
import { cloneDeep } from 'lodash';
import { GroupTypeDTO } from 'src/AngularJS/Admin/Company/company.model';
import { cvAssignmentLists } from '../../reusables/assign-tool/angular-assign-tool/angular-assign-tool.models';
import { CVApiService } from '../../reusables/services/cv-api.service';
import { GroupByMetric, MetricObjectiveObj, ObjectiveAssignmentDataObj, ObjectiveDetailsDataObj, ObjectiveLandingDataObj, ObjectivePaletteObj, ObjectiveProfileData } from './objective-landing/objective-landing.models';

@Injectable({
  providedIn: 'root'
})
export class ObjectivesService {
    showLandingMetricTab: boolean = true;
    showLandingGroupsTab: boolean = false;
    showLandingHeader: boolean = true;
    showAddEditObjective: boolean = false;
    showMetricCardView: boolean = true;
    showMetricSelectView: boolean = false;
    showGroupsCardView: boolean = false;
    isLoading: boolean = false;
    groupViewLoading: boolean = false;
    colorPalettes: ObjectivePaletteObj[] = [];
    empGroupLabels: GroupTypeDTO[] = [];
    recGroupLabels: GroupTypeDTO[] = [];
    allGroupLabels: GroupTypeDTO[] = [];
    metricAbrDict = null;
    loadedObjectives: ObjectiveAssignmentDataObj[] = null;
    profilesInLevel: ObjectiveProfileData[][] = null;
    groupObjByMetric: GroupByMetric[] = [];
    selectedGroupItem = null;
    selectedAttributeItem = null;
    selectedGroupCards = {
        'quota': [],
        'normalized': []
    };
    selectedMetric = null;
    selectedObjectives = null;
    selectedValueTab: string = "day";
    loadedGroups: any[] = [];
    addEdit = {
        originalObjectives: [],
        editedObjectives: null,
        selectedObjective: null,
        assignedGroups: null,
        originalAssignedGroups: null,
        MetricID: 0,
        MetricName: "",
        MetricValueType: null,
        MetricObjectiveType: null,
        MetricValueTimeType: null,
        MetricDirection: null,
        MetricIsRealtime: null,
        selectedRange: null
    }

    constructor(
        public cvApiService: CVApiService,
    ) { }

    public async LoadGroupTypes() {
        try {
            let response = await this.loadGroupTypes();

            if (response.status_code == 200) {
                this.empGroupLabels = angular.copy(response.data.EmpGroupTypes);
                this.recGroupLabels = angular.copy(response.data.RecGroupTypes);
                this.allGroupLabels = this.allGroupLabels.concat(this.empGroupLabels, this.recGroupLabels);
            }
            else {
                showErrorMessage(response.warnings[0]);
                this.empGroupLabels = [];
                this.recGroupLabels = [];
            }
        } catch (e) {
            console.error(e);
        }
    }

    private async loadGroupTypes() {
        return this.cvApiService.get<cvAssignmentLists>("/api/v1/group_types");
    }

    public async LoadGroupListRows(type) {
        try {
            let response = await this.loadGroupListRows(type);

            if (response.status_code == 200) {
                this.loadedGroups = response.data;
                this.groupViewLoading = false;
            }
            else {
                showErrorMessage(response.warnings[0]);
                this.loadedGroups = [];
                this.groupViewLoading = false;
            }
        } catch (e) {
            console.error(e);
        }
    }

    private async loadGroupListRows(type) {
        return this.cvApiService.get<[]>("/api/v1/objective_group_lists/row/" + type);
    }

    public async LoadMetricObjectiveType(metricID) {
        try {
            let response = await this.loadMetricObjectiveType(metricID);

            if (response.status_code == 200) {
                return response.data;
            }
            else {
                showErrorMessage(response.warnings[0]);
            }
        } catch (e) {
            console.error(e);
        }
    }

    private async loadMetricObjectiveType(metricID) {
        return this.cvApiService.get<MetricObjectiveObj>("/api/v1/metric_objective_type/" + metricID);
    }

    public async LoadColorPalettes() {
        try {
            let response = await this.loadColorPalettes();

            if (response.status_code == 200) {
                this.colorPalettes = angular.copy(response.data);
            }
            else {
                showErrorMessage(response.warnings[0]);
            }
        } catch (e) {
            console.error(e);
        }
    }

    private async loadColorPalettes() {
        return this.cvApiService.get<ObjectivePaletteObj[]>("/api/v1/objective_color_palettes/");
    }

    public async LoadObjectives() {
        try {
            this.isLoading = true;
            let response = await this.loadObjectives();

            if (response.status_code == 200) {
                this.metricAbrDict = angular.copy(response.data.metricAbrDict);
                this.loadedObjectives = angular.copy(response.data.assignmentData);
                this.profilesInLevel = angular.copy(response.data.profilesInLevel);

                // Get percent metricIds
                let percentMetricIds = new Set<number>();
                let metricKeys = Object.keys(this.metricAbrDict);
                for (let key of metricKeys) {
                    let metric = this.metricAbrDict[key];
                    if (metric.NumericType == 2)
                        percentMetricIds.add(metric.MetricID);
                };
                // Convert percent objective detail values to whole numbers
                for (let loadedObjective of this.loadedObjectives) {
                    if (percentMetricIds.has(loadedObjective.MetricID))
                        for (let objectiveDetail of loadedObjective.combinedObj.ObjectiveDetails)
                            if (!!objectiveDetail.dayMinValue)
                                objectiveDetail.dayMinValue *= 100;
                            else if (!!objectiveDetail.weekMinValue)
                                objectiveDetail.weekMinValue *= 100;
                            else if (!!objectiveDetail.monthMinValue)
                                objectiveDetail.monthMinValue *= 100;

                    loadedObjective.assignments.assignOptions = {};
                    loadedObjective.assignments.assignOptions.coreAssignAtLevel = loadedObjective.assignments.coreAssignAtLevel;
                    loadedObjective.assignments.assignOptions.coreAssignAtType = loadedObjective.assignments.coreAssignAtType;

                    if (this.metricAbrDict[loadedObjective.MetricID].MetricObjectiveType == 1)
                        loadedObjective.assignments.assignOptions.coreAssignAtOption = "normal";
                    else if (loadedObjective.assignments.coreAssignAtLevel < this.empGroupLabels.length && loadedObjective.assignments.emp.length > 0
                        && loadedObjective.assignments.emp[0].GroupLevel > loadedObjective.assignments.coreAssignAtLevel)
                        loadedObjective.assignments.assignOptions.coreAssignAtOption = "parent";
                    else
                        loadedObjective.assignments.assignOptions.coreAssignAtOption = "specific";

                    loadedObjective.combinedObj.StartDate = new Date(loadedObjective.combinedObj.StartDate);

                    for (let alerts of loadedObjective.combinedObj.AlertsList) {
                        for (let alert of alerts) {
                            alert.OtherEmailsList = alert.OtherEmails.split(',');
                        }
                    }
                }

                this.setGroupObjByMetric();
            }
            else
                showErrorMessage(response.warnings[0]);
        } catch (e) {
            console.error(e);
        } finally {
            this.isLoading = false;
        }
    }

    private async loadObjectives() {
        return this.cvApiService.get<ObjectiveLandingDataObj>("/api/v1/objective_load_Objectives/true");
    }

    public async SaveObjectives(assignedObj) {
        let editedObjectives = this.addEdit.editedObjectives;
        // Convert percent objective detail values from whole numbers to floating point
        if (this.addEdit.MetricValueType == 2) {
            editedObjectives = cloneDeep(this.addEdit.editedObjectives);
            for (var obj of editedObjectives)
                for (var od of obj.ObjectiveDetails) {
                    if (od.dayMinValue != null)
                        od.dayMinValue /= 100;
                    else if (od.weekMinValue != null)
                        od.weekMinValue /= 100;
                    else if (od.monthMinValue != null)
                        od.monthMinValue /= 100;

                    if (od.dayMaxValue != null)
                        od.dayMaxValue /= 100;
                    else if (od.weekMaxValue != null)
                        od.weekMaxValue /= 100;
                    else if (od.monthMaxValue != null)
                        od.monthMaxValue /= 100;
                }
        }

        try {
            let response = await this.cvApiService.post<number[]>("/api/v1/save_objectives/", JSON.stringify(
                {
                    "assignedObj": assignedObj,
                    "editedList": editedObjectives,
                    "metricID": this.addEdit.MetricID
                }
            ));

            if (response.status_code >= 200 && response.status_code < 300)
                return response;
            else throw response;
        } catch (error) {
            console.error(`Failed to save objective.`);
        }
    }

    public async removeObjectives(stringToRemove) {
        return this.cvApiService.delete("/api/v1/objective_Remove_Objectives/" + stringToRemove);
    }

    public addNewObjective(coreAssignAtLevel, coreAssignAtType) {
        let newObj: ObjectiveDetailsDataObj = {
            ObjectiveID: 0,
            CompanyID: 0,
            GroupingID: 0,
            ProfileName: "",
            StartDate: new Date(),
            EndDate: null,
            ObjectivePaletteID: 0,
            AssignedLevel: coreAssignAtLevel,
            AssignedType: coreAssignAtType,
            ObjectiveDetails: [],
            AlertsList: [[], [], []]
        }

        this.addEdit.editedObjectives.push(newObj);
    }

    public addQuickLevelObjective() {
        let newObj: ObjectiveDetailsDataObj = {
            ObjectiveID: 0,
            CompanyID: 0,
            GroupingID: 0,
            ProfileName: "",
            StartDate: new Date(),
            EndDate: null,
            ObjectivePaletteID: this.addEdit.editedObjectives[0] != null ? this.addEdit.editedObjectives[0].ObjectivePaletteID : 0,
            AssignedLevel: 0,
            AssignedType: null,
            ObjectiveDetails: [],
            AlertsList: [[], [], []]
        }

        this.addEdit.editedObjectives.push(newObj);
    }

    /**
     * Opens the specified objective for editing.
     * @param obj Objective object
     * @param fromMetric True if this is being done from the Metric View, false if done from the Groups View; defaults to true
     */
    public editObjective(obj: ObjectiveAssignmentDataObj, fromMetric = true) {
        if (!fromMetric)
            this.selectedMetric = angular.copy(this.metricAbrDict[obj.MetricID]);

        this.addEdit.selectedObjective = 0;
        this.addEdit.MetricID = this.selectedMetric.metricID ?? this.selectedMetric.MetricID;
        this.addEdit.MetricName = this.selectedMetric.name ?? this.selectedMetric.MetricName;
        this.addEdit.MetricValueType = this.selectedMetric.numericType ?? this.selectedMetric.NumericType;
        this.addEdit.MetricObjectiveType = this.selectedMetric.objectiveType ?? this.selectedMetric.MetricObjectiveType;
        this.addEdit.MetricValueTimeType = this.selectedMetric.timeType ?? this.selectedMetric.TimeType;
        this.addEdit.MetricDirection = this.selectedMetric.direction ?? this.selectedMetric.Direction;
        this.addEdit.MetricIsRealtime = this.selectedMetric.isRealTime ?? this.selectedMetric.IsRealtime;

        this.handleAssignUpdate(obj.assignments); // handleAssignUpdate() replaces this.objectiveService.addEdit.assignedGroups with the inputted parameter (in this case, obj.assignments)
        this.addEdit.originalAssignedGroups = angular.copy(obj.assignments);

        this.showAddEditObjective = true;
        this.showLandingHeader = false;

        if (fromMetric) {
            this.showMetricCardView = true;
            this.showMetricSelectView = false;
        }
        else
            this.showGroupsCardView = false;

        for(let obj of this.addEdit.editedObjectives) {
            for (let detail of obj.ObjectiveDetails) {
                if (detail.dayMinValue)
                    detail.dayMinValue = this.getFormattedValue(detail.dayMinValue, this.addEdit.MetricValueType, this.addEdit.MetricValueTimeType);

                if (detail.weekMinValue)
                    detail.weekMinValue = this.getFormattedValue(detail.weekMinValue, this.addEdit.MetricValueType, this.addEdit.MetricValueTimeType);

                if (detail.monthMinValue)
                    detail.monthMinValue = this.getFormattedValue(detail.monthMinValue, this.addEdit.MetricValueType, this.addEdit.MetricValueTimeType);
            }
        }
    }

    public setGroupObjByMetric() {
        this.groupObjByMetric = [];

        for (let loadedObjective of this.loadedObjectives) {
            var metricFound = false;
            for (let groupByMetric of this.groupObjByMetric) {
                if (groupByMetric.metricID == loadedObjective.MetricID) {
                    metricFound = true;

                    if (loadedObjective.combinedObj.GroupingID == 0)
                        groupByMetric.count++;
                    break;
                }
            }

            if (!metricFound) {
                this.groupObjByMetric.push({
                    'metricID': loadedObjective.MetricID,
                    'name': this.metricAbrDict[loadedObjective.MetricID].MetricName,
                    'count': 1,
                    'objectiveType': this.metricAbrDict[loadedObjective.MetricID].MetricObjectiveType,
                    'numericType': this.metricAbrDict[loadedObjective.MetricID].NumericType,
                    'timeType': this.metricAbrDict[loadedObjective.MetricID].TimeType,
                    'direction': this.metricAbrDict[loadedObjective.MetricID].Direction,
                });
            }
        }

        // Sort groupObjByMetric
        this.groupObjByMetric.sort((a, b) => (a.name.toLocaleLowerCase() > b.name.toLocaleLowerCase()) ? 1 : -1);
    }

    public handleAssignUpdate(newValue) { // Test if the emit updates the variable immediately or if I need to set it based on the data emitted ie old vs new value
        if (!newValue || !newValue.coreAssignAtLevel || !newValue.coreAssignAtType)
            return;

        if (!this.addEdit.assignedGroups || !this.addEdit.assignedGroups.coreAssignAtLevel || this.addEdit.assignedGroups.coreAssignAtType != newValue.coreAssignAtType) {
            this.addEdit.editedObjectives = [];
            this.addNewObjective(newValue.coreAssignAtLevel, newValue.coreAssignAtType);

            for (let i = newValue.coreAssignAtLevel - 1; i > 0; i--) {
                this.addQuickLevelObjective();
            }

            this.selectObjective(0);
        }
        else {
            if (this.addEdit.assignedGroups.coreAssignAtLevel > newValue.coreAssignAtLevel) { // If new level is lower then remove any cards that are no longer valid
                this.addEdit.editedObjectives.splice(0, this.addEdit.assignedGroups.coreAssignAtLevel - newValue.coreAssignAtLevel);

                if (this.addEdit.editedObjectives[0] == null || this.addEdit.editedObjectives[0].AssignedLevel <= 1) {
                    this.addEdit.editedObjectives.splice(0, 1);

                    let newObj = {
                        ObjectiveID: 0,
                        CompanyID: 0,
                        GroupingID: 0,
                        StartDate: new Date(),
                        EndDate: null,
                        ObjectivePaletteID: this.addEdit.editedObjectives[0] != null ? this.addEdit.editedObjectives[0].ObjectivePaletteID : 0,
                        AssignedLevel: newValue.coreAssignAtLevel,
                        AssignedType: newValue.coreAssignAtType,
                        ObjectiveDetails: [],
                        AlertsList: [[], [], []]
                    };

                    this.addEdit.editedObjectives.splice(0, 0, newObj);
                }

                this.selectObjective(0);
            }
            else if (this.addEdit.assignedGroups.coreAssignAtLevel < newValue.coreAssignAtLevel) { // If new level is higher then push the highest level as new core and any needed quick add cards
                if (this.addEdit.editedObjectives.length < newValue.coreAssignAtLevel) {
                    let tempArr = angular.copy(this.addEdit.editedObjectives);
                    this.addEdit.editedObjectives = [];
                    this.addNewObjective(newValue.coreAssignAtLevel, newValue.coreAssignAtType);

                    for (let i = newValue.coreAssignAtLevel - 1; i > this.addEdit.assignedGroups.coreAssignAtLevel; i--) {
                        this.addQuickLevelObjective();
                    }

                    this.addEdit.editedObjectives = this.addEdit.editedObjectives.concat(tempArr);

                    this.selectObjective(0);
                }
            }
            else if (this.addEdit.editedObjectives == null) {
                this.addEdit.editedObjectives = [];

                let newObj = {
                    ObjectiveID: 0,
                    CompanyID: 0,
                    GroupingID: 0,
                    StartDate: new Date(),
                    EndDate: null,
                    ObjectivePaletteID: this.addEdit.editedObjectives[0] != null ? this.addEdit.editedObjectives[0].ObjectivePaletteID : 0,
                    AssignedLevel: newValue.coreAssignAtLevel,
                    AssignedType: newValue.coreAssignAtType,
                    ObjectiveDetails: [],
                    AlertsList: [[], [], []]
                };

                this.addEdit.editedObjectives[0] = newObj;
            }
            else if (this.addEdit.editedObjectives[0] == null || this.addEdit.editedObjectives[0].AssignedLevel <= 1) {
                const objectiveDetails = this.addEdit.editedObjectives[0]?.ObjectiveDetails ?? [];
                this.addEdit.editedObjectives.splice(0, 1);

                let newObj = {
                    ObjectiveID: 0,
                    CompanyID: 0,
                    GroupingID: 0,
                    StartDate: new Date(),
                    EndDate: null,
                    ObjectivePaletteID: this.addEdit.editedObjectives[0] != null ? this.addEdit.editedObjectives[0].ObjectivePaletteID : 0,
                    AssignedLevel: newValue.coreAssignAtLevel,
                    AssignedType: newValue.coreAssignAtType,
                    ObjectiveDetails: objectiveDetails,
                    AlertsList: [[], [], []]
                };

                this.addEdit.editedObjectives.splice(0, 0, newObj);
            }
        }

        if (((this.addEdit.assignedGroups == null || this.addEdit.assignedGroups.coreAssignAtType != newValue.coreAssignAtType) && this.addEdit.selectedRange == null)
            || (this.addEdit.editedObjectives[this.addEdit.selectedObjective].ObjectiveDetails.length == 0 && this.addEdit.originalObjectives.length == 0)) {
            // The default palette is the first one to contain 3 levels. If none of those exist, then it just uses whatever palette is first.
            let threeRangePalette = this.colorPalettes.find(o => o.LevelCount == 3);
            this.addEdit.selectedRange = angular.copy(threeRangePalette ?? this.colorPalettes[0]);
            this.addEdit.editedObjectives[this.addEdit.selectedObjective].ObjectivePaletteID = this.addEdit.selectedRange.ObjectivePaletteID;

            this.addEdit.editedObjectives[this.addEdit.selectedObjective].ObjectiveDetails = [];
            for (let i = 0; i < this.addEdit.selectedRange.ObjectiveColors.length - 1; i++) {
                this.addEdit.editedObjectives[this.addEdit.selectedObjective].ObjectiveDetails.push(
                    { "dayMinValue": null, "dayMaxValue": null, "weekMinValue": null, "weekMaxValue": null, "monthMinValue": null, "monthMaxValue": null }
                );
            }
        }

        if (this.addEdit.assignedGroups != null && this.addEdit.assignedGroups.assignOptions != null)
            newValue.assignOptions = angular.copy(this.addEdit.assignedGroups.assignOptions);
        this.addEdit.assignedGroups = angular.copy(newValue);

        // Check for duplicate of a previously saved value
        this.checkLoadedObj();
    }

    public selectObjective(idx) {
        this.addEdit.selectedObjective = idx;

        if (this.addEdit.selectedRange != null && (this.addEdit.editedObjectives[this.addEdit.selectedObjective].ObjectivePaletteID == 0 ||
            this.addEdit.editedObjectives[this.addEdit.selectedObjective].ObjectiveDetails.length == 0)) {
            this.addEdit.editedObjectives[this.addEdit.selectedObjective].ObjectivePaletteID = this.addEdit.selectedRange.ObjectivePaletteID;

            this.addEdit.editedObjectives[this.addEdit.selectedObjective].ObjectiveDetails = [];
            for (let i = 0; i < this.addEdit.selectedRange.ObjectiveColors.length - 1; i++) {
                this.addEdit.editedObjectives[this.addEdit.selectedObjective].ObjectiveDetails.push(
                    { "dayMinValue": null, "dayMaxValue": null, "weekMinValue": null, "weekMaxValue": null, "monthMinValue": null, "monthMaxValue": null }
                );
            }
        }
    }

    public checkLoadedObj() {
        for (let loadedObjective of this.loadedObjectives) {
            // If there is a saved objective with the same metric, assign level, assign type, and the same length for the 3 assignment lists then
            if (loadedObjective.MetricID == this.addEdit.MetricID &&
                this.addEdit.editedObjectives.length > 0 &&
                loadedObjective.combinedObj.AssignedLevel == this.addEdit.editedObjectives[0].AssignedLevel &&
                loadedObjective.combinedObj.AssignedType == this.addEdit.editedObjectives[0].AssignedType &&
                this.addEdit.assignedGroups.emp != null && this.addEdit.assignedGroups.rec != null &&
                loadedObjective.assignments.emp.length == this.addEdit.assignedGroups.emp.length &&
                loadedObjective.assignments.rec.length == this.addEdit.assignedGroups.rec.length &&
                loadedObjective.assignments.attr.length == this.addEdit.assignedGroups.attr.length) {
                // Check each assigment list to ensure that they match exactly (order doesnt matter thought it may be fixed by id)
                let differenceFound = false;

                for (let assignedEmpGroups of this.addEdit.assignedGroups.emp) {
                    let matchFound = false;
                    for (let loadedEmpAssignment of loadedObjective.assignments.emp) {
                        if (loadedEmpAssignment.GroupID == assignedEmpGroups.GroupID && loadedEmpAssignment.GroupLevel == assignedEmpGroups.GroupLevel) {
                            matchFound = true;
                            break;
                        }
                    }

                    if (!matchFound) {
                        differenceFound = true;
                        break;
                    }
                }

                if (!differenceFound) {
                    for (let assignedRecGroups of this.addEdit.assignedGroups.rec) {
                        let matchFound = false;
                        for (let loadedRecAssignment of loadedObjective.assignments.rec) {
                            if (loadedRecAssignment.GroupID == assignedRecGroups.GroupID && loadedRecAssignment.GroupLevel == assignedRecGroups.GroupLevel) {
                                matchFound = true;
                                break;
                            }
                        }

                        if (!matchFound) {
                            differenceFound = true;
                            break;
                        }
                    }
                }

                if (!differenceFound) {
                    for (let assignedAttributeGroup of this.addEdit.assignedGroups.attr) {
                        let matchFound = false;
                        for (let loadedAttributeAssignment of loadedObjective.assignments.attr) {
                            if (loadedAttributeAssignment.AgentAttributeID == assignedAttributeGroup.AgentAttributeID && loadedAttributeAssignment.AttributeValueID == assignedAttributeGroup.AttributeValueID) {
                                matchFound = true;
                                break;
                            }
                        }

                        if (!matchFound) {
                            differenceFound = true;
                            break;
                        }
                    }
                }

                if (!differenceFound) { // Identical saved objective found so load it
                    // Check if the match was on a sub card and if so then load the core card.
                    if (loadedObjective.combinedObj.GroupingID != 0) { // Subcard so load its core
                        for (let loadedObjectiveSub of this.loadedObjectives) {
                            if (loadedObjectiveSub.combinedObj.ObjectiveID == loadedObjective.combinedObj.GroupingID) {
                                // Load stuff here for the core card
                                this.addEdit.originalObjectives = [];

                                this.addEdit.originalObjectives.push(angular.copy(loadedObjectiveSub.combinedObj));

                                // Add any sub cards
                                for (let loadedObjectiveTri of this.loadedObjectives) {
                                    if (loadedObjectiveTri.combinedObj.GroupingID == loadedObjectiveSub.combinedObj.ObjectiveID)
                                        this.addEdit.originalObjectives.push(angular.copy(loadedObjectiveTri.combinedObj));
                                }

                                this.addEdit.editedObjectives[0] = angular.copy(loadedObjectiveSub.combinedObj);
                                // Add any sub cards
                                for (let loadedObjectiveTri of this.loadedObjectives) {
                                    if (loadedObjectiveTri.combinedObj.GroupingID == loadedObjectiveSub.combinedObj.ObjectiveID)
                                        this.addEdit.editedObjectives[loadedObjectiveSub.combinedObj.AssignedLevel - loadedObjectiveTri.combinedObj.AssignedLevel] = angular.copy(loadedObjectiveTri.combinedObj);
                                }

                                //fix loaded value (same logic used in the card click method)
                                for(let obj of this.addEdit.editedObjectives) {
                                    for (let detail of obj.ObjectiveDetails) {
                                        if (detail.dayMinValue)
                                            detail.dayMinValue = this.getFormattedValue(detail.dayMinValue, this.addEdit.MetricValueType, this.addEdit.MetricValueTimeType);

                                        if (detail.weekMinValue)
                                            detail.weekMinValue = this.getFormattedValue(detail.weekMinValue, this.addEdit.MetricValueType, this.addEdit.MetricValueTimeType);

                                        if (detail.monthMinValue)
                                            detail.monthMinValue = this.getFormattedValue(detail.monthMinValue, this.addEdit.MetricValueType, this.addEdit.MetricValueTimeType);
                                    }
                                }

                                let pal = null;
                                for (let colorPalette of this.colorPalettes) {
                                    if (colorPalette.ObjectivePaletteID == this.addEdit.editedObjectives[0].ObjectivePaletteID) {
                                        pal = angular.copy(colorPalette);
                                        break;
                                    }
                                }

                                if (pal != null) {
                                    this.addEdit.selectedRange = pal;

                                    // When adding multi change the 1 here to length of created + 1
                                    for (let i = 1; i < this.addEdit.editedObjectives.length; i++) {
                                        if (this.addEdit.editedObjectives[i].ObjectivePaletteID != this.addEdit.selectedRange.ObjectivePaletteID) {
                                            this.addEdit.editedObjectives[i].ObjectivePaletteID = this.addEdit.selectedRange.ObjectivePaletteID;
                                            this.addEdit.editedObjectives[i].ObjectiveDetails = [];

                                            for (let j = 0; j < this.addEdit.selectedRange.ObjectiveColors.length - 1; j++) {
                                                this.addEdit.editedObjectives[i].ObjectiveDetails.push({ "dayMinValue": null, "weekMinValue": null, "monthMinValue": null });
                                            }
                                        }
                                    }
                                }

                                return;
                            }
                        }
                    }
                    else {
                        // Load stuff here
                        this.addEdit.originalObjectives = [];

                        this.addEdit.originalObjectives.push(angular.copy(loadedObjective.combinedObj));

                        // Add any sub cards
                        for (let loadedObjectivesSub of this.loadedObjectives) {
                            if (loadedObjectivesSub.combinedObj.GroupingID == loadedObjective.combinedObj.ObjectiveID)
                                this.addEdit.originalObjectives.push(angular.copy(loadedObjectivesSub.combinedObj));
                        }

                        let len = this.addEdit.editedObjectives.length;
                        this.addEdit.editedObjectives = [];
                        for (let i = 0; i < len; i++) {
                            this.addQuickLevelObjective();
                        }

                        this.addEdit.editedObjectives[0] = angular.copy(loadedObjective.combinedObj);
                        // Add any sub cards
                        for (let loadedObjectivesSub of this.loadedObjectives) {
                            if (loadedObjectivesSub.combinedObj.GroupingID == loadedObjective.combinedObj.ObjectiveID)
                                this.addEdit.editedObjectives[loadedObjective.combinedObj.AssignedLevel - loadedObjectivesSub.combinedObj.AssignedLevel] = angular.copy(loadedObjectivesSub.combinedObj);
                        }

                        //fix loaded value (same logic used in the card click method)
                        for(let obj of this.addEdit.editedObjectives) {
                            for (let detail of obj.ObjectiveDetails) {
                                if (detail.dayMinValue)
                                    detail.dayMinValue = this.getFormattedValue(detail.dayMinValue, this.addEdit.MetricValueType, this.addEdit.MetricValueTimeType);

                                if (detail.weekMinValue)
                                    detail.weekMinValue = this.getFormattedValue(detail.weekMinValue, this.addEdit.MetricValueType, this.addEdit.MetricValueTimeType);

                                if (detail.monthMinValue)
                                    detail.monthMinValue = this.getFormattedValue(detail.monthMinValue, this.addEdit.MetricValueType, this.addEdit.MetricValueTimeType);
                            }
                        }

                        let pal = null;
                        for (let colorPalette of this.colorPalettes) {
                            if (colorPalette.ObjectivePaletteID == this.addEdit.editedObjectives[0].ObjectivePaletteID) {
                                pal = angular.copy(colorPalette);
                                break;
                            }
                        }

                        if (pal != null) {
                            this.addEdit.selectedRange = pal;

                            // When adding multi change the 1 here to length of created + 1
                            for (let i = 1; i < this.addEdit.editedObjectives.length; i++) {

                                if (this.addEdit.editedObjectives[i].ObjectivePaletteID != this.addEdit.selectedRange.ObjectivePaletteID) {
                                    this.addEdit.editedObjectives[i].ObjectivePaletteID = this.addEdit.selectedRange.ObjectivePaletteID;
                                    this.addEdit.editedObjectives[i].ObjectiveDetails = [];

                                    for (let j = 0; j < this.addEdit.selectedRange.ObjectiveColors.length - 1; j++) {
                                        this.addEdit.editedObjectives[i].ObjectiveDetails.push({ "dayMinValue": null, "weekMinValue": null, "monthMinValue": null });
                                    }
                                }
                            }
                        }

                        return;
                    }
                }
            }
        }
    }

    /**
     * Returns the inputted value in the specified format.
     * @param value Value to format; will default to 0 if anything other than a number or a string in HH:MM:SS format is used
     * @param metricValueType 0: Currency, 1: Number, 2: Percent, 3: Time
     * @param metricValueTimeType Only used when metricValueType is set to 3; 0: None, 1: Second, 2: Minute, 3: Hour, 4: Time (HH:MM:SS)
     */
    public getFormattedValue(value, metricValueType: number, metricValueTimeType?: number): number | string {
        let formatted;

        // If the input is in time format (00:00:00), convert it to the number of seconds that it represents
        if ((!value || isNaN(value)) && value != 0)
            value = this.getSecondsFromTimeType(value);

        switch (metricValueType) {
            case 0: // Currency - This one could probably be changed to force two decimal places...
            case 1: // Number
            case 2: // Percent
                formatted = value;
                break;
            case 3: // Time
                switch (metricValueTimeType) {
                    case 0: // None
                    case 1: // Second
                    case 2: // Minute
                    case 3: // Hour
                        formatted = value;
                        break;
                    case 4: // Time
                        let hours = Math.floor(value / 3600);
                        let minutes = Math.floor((value - (hours * 3600)) / 60);
                        let seconds = value - (hours * 3600) - (minutes * 60);

                        let hoursString = hours.toString();
                        let minutesString = minutes.toString();
                        let secondsString = seconds.toString();

                        if (hours < 10)
                            hoursString = "0" + hours;
                        if (minutes < 10)
                            minutesString = "0" + minutes;
                        if (seconds < 10)
                            secondsString = "0" + seconds;
                        formatted = `${hoursString}:${minutesString}:${secondsString}`;
                        break;
                    default:
                        formatted = value;
                        break;
                }
                break;
            default:
                formatted = value;
                break;
        }

        return formatted;
    }

    /**
     * Converts the specified formatted value into the numeric value it represents based on currentNumericType and currentTimeType.
     * @param formattedValue String in format that matches current selected metric data type
     * @param metricValueType 0: Currency, 1: Number, 2: Percent, 3: Time - defaults to 1
     * @param metricValueTimeType Only used when metricValueType is set to 3; 0: None, 1: Second, 2: Minute, 3: Hour, 4: Time (HH:MM:SS) - defaults to 1
     */
    public convertFormattedValueToNumber(formattedValue: string, metricValueType: number, metricValueTimeType?: number): number {
        switch (metricValueType) {
            case 0: // Currency
            case 1: // Number
            case 2: // Percent
                return +formattedValue;
            case 3: // Time
                switch (metricValueTimeType) {
                    case 0: // None
                    case 1: // Second
                    case 2: // Minute
                    case 3: // Hour
                        return +formattedValue;
                    case 4: // Time
                        return this.getSecondsFromTimeType(formattedValue);
                    default:
                        return +formattedValue;
                }
            default:
                return +formattedValue;
        }
    }

    /**
     * Returns the number of seconds represented by a string in HH:MM:SS format. Returns 0 if the string is invalid.
     * @param value A string in HH:MM:SS format. The hours component is typically expected to have two digits, but any amount greater than zero is acceptable.
     */
    public getSecondsFromTimeType(value: string) {
        if(value == null)
            return 0;

        let test = value.match(/^\d+:\d{2}:\d{2}$/g);
        if (test && test.length > 0) {
            let array = [...value.matchAll(/\d+/g)];
            return (parseInt(array[0].toString()) * 3600) + (parseInt(array[1].toString()) * 60) + parseInt(array[2].toString());
        }
        else
            return 0;
    }
}

/*.directive('objectiveTimeFormatter', function ($timeout) {
    return {
        restrict: 'A',
        require: 'ngModel',
        link: function (scope, element, attr, ngModel) {

            element.on('blur', conversionFunction.bind(null, ngModel));

            function conversionFunction(ngModel) {
                $timeout(function () {

                    if (ngModel == null) return;

                    var value = ngModel.$modelValue;

                    var type = scope.$parent.$parent.ctrlObjectivesAddEdit.of.addEdit.MetricValueTimeType;
                    var formatted;

                    if (value == null || value == "") return "";

                    switch (type) {
                        case 1: //Second
                            formatted = value;
                            break;
                        case 2: //Minute
                            //formatted = value / 60;
                            formatted = value;
                            break;
                        case 3: //Hour
                            //formatted = Math.floor((value / 3600) * 100) / 100;
                            formatted = value;
                            break;
                        case 4: //Time
                            var hours = Math.floor(value / 3600);
                            var minutes = Math.floor((value - (hours * 3600)) / 60);
                            var seconds = value - (hours * 3600) - (minutes * 60);

                            if (hours < 10) { hours = "0" + hours; }
                            if (minutes < 10) { minutes = "0" + minutes; }
                            if (seconds < 10) { seconds = "0" + seconds; }
                            formatted = hours + ':' + minutes + ':' + seconds;
                            break;
                        case 5: //Percent
                            formatted = (value * 100).toFixed(2);
                            break;
                        default:
                            formatted = value;
                            break;
                    }

                    ngModel.$viewValue = formatted;
                    ngModel.$render();
                }, 0);
            }

            ngModel.$formatters.push(function (value) {
                var type = scope.$parent.$parent.ctrlObjectivesAddEdit.of.addEdit.MetricValueTimeType;
                var formatted;

                if (value == null || value == "") return "";

                switch (type) {
                    case 1: //Second
                        formatted = value;
                        break;
                    case 2: //Minute
                        //formatted = value / 60;
                        formatted = value;
                        break;
                    case 3: //Hour
                        //formatted = Math.floor((value / 3600) * 100) / 100;
                        formatted = value;
                        break;
                    case 4: //Time
                        var hours = Math.floor(value / 3600);
                        var minutes = Math.floor((value - (hours * 3600)) / 60);
                        var seconds = value - (hours * 3600) - (minutes * 60);

                        if (hours < 10) { hours = "0" + hours; }
                        if (minutes < 10) { minutes = "0" + minutes; }
                        if (seconds < 10) { seconds = "0" + seconds; }
                        formatted = hours + ':' + minutes + ':' + seconds;
                        break;
                    case 5: //Percent
                        formatted = (value * 100).toFixed(2);
                        break;
                    default:
                        formatted = value;
                        break;
                }

                return formatted;
            });
            ngModel.$parsers.push(function (value) {
                var type = scope.$parent.$parent.ctrlObjectivesAddEdit.of.addEdit.MetricValueTimeType;
                var formatted;

                if (value == null || value == "") return "";

                switch (type) {
                    case 1: //Second
                        formatted = value;
                        break;
                    case 2: //Minute
                        //formatted = value * 60;
                        formatted = value;
                        break;
                    case 3: //Hour
                        //formatted = value * 3600;
                        formatted = value;
                        break;
                    case 4: //Time
                        var values = value.split(":");
                        if (values.length == 3)
                            formatted = (parseInt(values[0]) * 3600) + (parseInt(values[1]) * 60) + (parseInt(values[2]));
                        else if (values.length == 2)
                            formatted = (parseInt(values[0]) * 60) + (parseInt(values[1]));
                        else
                            formatted = parseInt(values[0]);
                        break;
                    case 5: //Percent
                        formatted = value / 100;
                        break;
                    default:
                        formatted = value;
                        break;
                }

                //conversionFunction(formatted);

                return formatted;
            });
        }
    };
})*/
